import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import axios from "axios";
import { triggerAlert, ConfirmationAlert } from "../../utils";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import Cookies from "js-cookie";

/**
 * Billing → Subscriptions (observe-only).
 *
 * Lists every org's billing.AgentSubscription via /api/admin/agent-subscriptions/.
 * Admins can filter, review, and cancel a subscription. No manual creation —
 * subscriptions originate from the customer portal checkout.
 */

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "past_due", label: "Past Due" },
  { value: "cancelled", label: "Cancelled" },
  { value: "exhausted", label: "Exhausted" },
];

const CATEGORY_OPTIONS = [
  { value: "calling", label: "Calling" },
  { value: "avatar", label: "Avatar" },
  { value: "workflow", label: "Workflow" },
  { value: "phone_number", label: "Phone Number" },
];

const categoryLabel = (v) => (CATEGORY_OPTIONS.find((c) => c.value === v) || {}).label || v;

export default function AgentSubscriptions() {
  document.title = "Digital Employees - Subscriptions";
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchData = useCallback(
    async (page = 1, size = perPage) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          page_number: page,
          page_size: size,
        });
        if (statusFilter) params.append("status", statusFilter);
        if (categoryFilter) params.append("category", categoryFilter);
        if (search.trim()) params.append("search", search.trim());
        const res = await axios.get(
          `/api/admin/agent-subscriptions/?${params.toString()}`,
          authHeaders()
        );
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        data.forEach((r, i) => { r.Num = (page - 1) * size + i + 1; });
        setRows(data);
        setTotalRows(res.data?.count || data.length);
      } catch (err) {
        setRows([]);
        setTotalRows(0);
        triggerAlert("error", "Oops...", "Failed to load subscriptions.");
      } finally {
        setIsLoading(false);
      }
    },
    [perPage, statusFilter, categoryFilter, search]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1, perPage);
    }, 400);
    return () => clearTimeout(t);
  }, [fetchData, perPage]);

  const cancelSubscription = (row) => {
    ConfirmationAlert(
      `Cancel the subscription for "${row.organization_name}" — ${row.agent_name}?`,
      "Cancel subscription",
      async () => {
        try {
          await axios.post(
            `/api/admin/agent-subscriptions/${row.id}/cancel/`,
            { reason: "Cancelled by admin" },
            authHeaders()
          );
          triggerAlert("success", "Success", "Subscription cancelled.");
          fetchData(currentPage, perPage);
        } catch (err) {
          triggerAlert(
            "error",
            "Oops...",
            err?.response?.data?.error || "Could not cancel the subscription."
          );
        }
      }
    );
  };

  const statusBadge = (s) => {
    const map = {
      active: "btn-success activebtn",
      cancelled: "btn-secondary",
      past_due: "btn-danger Inactivebtn",
      exhausted: "btn-warning",
    };
    return (
      <span className={`btn btn-sm btn-rounded waves-effect waves-light ${map[s] || "btn-info"}`}>
        {(s || "").replace("_", " ")}
      </span>
    );
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Organization", sortable: true, selector: (r) => r.organization_name,
      cell: (r) => <span>{r.organization_name}</span> },
    { name: "Agent / Plan", sortable: true, selector: (r) => r.agent_name,
      cell: (r) => <span>{r.agent_name}</span> },
    { name: "Category", selector: (r) => r.category, cell: (r) => <span>{categoryLabel(r.category)}</span> },
    { name: "Status", selector: (r) => r.status, cell: (r) => statusBadge(r.status) },
    { name: "Price", selector: (r) => Number(r.price),
      cell: (r) => <span style={{ fontWeight: 600 }}>${Number(r.price || 0).toFixed(2)}</span> },
    { name: "Next Billing", selector: (r) => r.billing_date,
      cell: (r) => <span>{r.billing_date ? new Date(r.billing_date).toLocaleDateString() : "—"}</span> },
    { name: "ARB ID", selector: (r) => r.arb_subscription_id,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{r.arb_subscription_id || "—"}</span> },
    {
      name: "Action",
      width: "180px",
      cell: (r) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate(`/billing/subscriptions/${r.id}`)}
          >
            View
          </button>
          {r.status !== "cancelled" && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => cancelSubscription(r)}>
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: { style: { backgroundColor: "#064c77", minHeight: "52px" } },
    headCells: { style: { color: "#fff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "56px" } },
    cells: { style: { fontSize: "14px", color: "#495057" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Subscriptions" heading="Billing / Subscriptions" />
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label mb-1 font_weight_500">Status</label>
                        <select className="form-select" value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="">All statuses</option>
                          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label mb-1 font_weight_500">Category</label>
                        <select className="form-select" value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}>
                          <option value="">All categories</option>
                          {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label mb-1 font_weight_500">Search</label>
                        <input type="search" className="form-control" placeholder="Organization, agent or ARB id..."
                          value={search} onChange={(e) => setSearch(e.target.value)} />
                      </div>
                    </div>

                    {!isLoading ? (
                      <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={currentPage}
                        onChangePage={(page) => { setCurrentPage(page); fetchData(page, perPage); }}
                        onChangeRowsPerPage={(newPer, page) => { setPerPage(newPer); fetchData(page, newPer); }}
                        customStyles={customStyles}
                        persistTableHead
                      />
                    ) : (
                      <div className="loader-overlay text-white"><Loader /></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
